import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteUserDocument,
  downloadUserDocument,
  getUserDocumentFile,
  getUserDocuments,
  uploadUserDocument,
} from '../../api';
import { UserDocument, UserDocumentType } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { createUuid } from '../../utils/createUuid';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import DocumentPreviewModal from '../DocumentPreviewModal/DocumentPreviewModal';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import './ProfileDocumentsFiles.css';

const DOC_TYPE_OPTIONS: { value: UserDocumentType; label: string }[] = [
  { value: 'ppl', label: 'Свидетельство частного пилота' },
  { value: 'vlek', label: 'ВЛЭК' },
  { value: 'skills', label: 'Справка о проверке навыков' },
  { value: 'other', label: 'Иные документы' },
];

const REPLACEABLE_DOC_TYPES: UserDocumentType[] = ['ppl', 'vlek', 'skills'];
const TYPE_ORDER: Record<string, number> = {
  ppl: 0,
  vlek: 1,
  skills: 2,
  other: 3,
};

const docTypeLabel = (docType: string) =>
  DOC_TYPE_OPTIONS.find((o) => o.value === docType)?.label || docType;

async function dropReplacedDocuments(
  userId: number,
  docType: UserDocumentType,
  keepGroupId: string
) {
  if (!REPLACEABLE_DOC_TYPES.includes(docType)) return;
  const res = await getUserDocuments(userId);
  const keep = keepGroupId.toLowerCase();
  const stale = (res.data || []).filter(
    (item) =>
      item.doc_type === docType &&
      String(item.group_id || '').toLowerCase() !== keep
  );
  await Promise.all(
    stale.map((item) =>
      deleteUserDocument(userId, item.id).catch(() => undefined)
    )
  );
}

type DocGroup = {
  key: string;
  doc_type: string;
  created_at: string | null;
  files: UserDocument[];
};

function groupDocuments(docs: UserDocument[]): DocGroup[] {
  const others: UserDocument[] = [];
  const map = new Map<string, DocGroup>();
  for (const doc of docs) {
    if (doc.doc_type === 'other') {
      others.push(doc);
      continue;
    }
    const key = doc.group_id || `solo-${doc.id}`;
    const existing = map.get(key);
    if (existing) {
      existing.files.push(doc);
      if (
        doc.created_at &&
        (!existing.created_at || doc.created_at > existing.created_at)
      ) {
        existing.created_at = doc.created_at;
      }
    } else {
      map.set(key, {
        key,
        doc_type: doc.doc_type,
        created_at: doc.created_at,
        files: [doc],
      });
    }
  }
  const groups = Array.from(map.values());
  if (others.length > 0) {
    groups.push({
      key: 'other-all',
      doc_type: 'other',
      created_at: others[0]?.created_at ?? null,
      files: others,
    });
  }
  groups.sort(
    (a, b) => (TYPE_ORDER[a.doc_type] ?? 9) - (TYPE_ORDER[b.doc_type] ?? 9)
  );
  return groups;
}

type Props = {
  userId: number;
  canEdit: boolean;
  dateFormat: 'dot' | 'long';
};

const ProfileDocumentsFiles = ({ userId, canEdit, dateFormat }: Props) => {
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [docType, setDocType] = useState<UserDocumentType>('ppl');
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<UserDocument | null>(null);
  const [previewFiles, setPreviewFiles] = useState<UserDocument[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const previewRequest = useRef(0);

  const groups = useMemo(() => groupDocuments(documents), [documents]);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await getUserDocuments(userId);
      setDocuments(res.data || []);
    } catch {
      setDocuments([]);
    }
  }, [userId]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const uploadFiles = async (files: File[], type: UserDocumentType) => {
    setUploading(true);
    const groupId = createUuid();
    let okCount = 0;
    try {
      for (const file of files) {
        await uploadUserDocument(userId, file, type, groupId);
        okCount += 1;
      }
      await dropReplacedDocuments(userId, type, groupId);
      await loadDocuments();
      showSuccess(
        okCount === 1
          ? 'Документ загружен'
          : `Загружено файлов: ${okCount}`
      );
    } catch (error) {
      if (okCount > 0) {
        await dropReplacedDocuments(userId, type, groupId);
        await loadDocuments();
      }
      showError(
        getApiErrorMessage(
          error,
          okCount > 0
            ? `Загружено ${okCount}, остальные не удалось`
            : 'Не удалось загрузить документ'
        )
      );
    }
    setUploading(false);
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || !canEdit) return;
    const replacing =
      REPLACEABLE_DOC_TYPES.includes(docType) &&
      documents.some((item) => item.doc_type === docType);
    if (replacing) {
      setPendingFiles(files);
      return;
    }
    void uploadFiles(files, docType);
  };

  const handleDownload = async (doc: UserDocument) => {
    try {
      await downloadUserDocument(
        userId,
        doc.id,
        doc.original_name || doc.filename
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось скачать документ'));
    }
  };

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewDoc(null);
    setPreviewFiles([]);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewKind('other');
    setPreviewUrl(null);
  };

  const handlePreview = async (
    doc: UserDocument,
    files: UserDocument[] = [doc]
  ) => {
    setPreviewFiles(files);
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    const name = doc.original_name || doc.filename;
    const kind = previewKindFromName(name);
    setPreviewUrl(null);
    setPreviewDoc(doc);
    setPreviewKind(kind);
    setPreviewError(null);
    if (kind === 'other') {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getUserDocumentFile(userId, doc.id);
      if (previewRequest.current !== requestId) return;
      const blob = blobWithMime(res.data, name);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(
        getApiErrorMessage(error, 'Не удалось открыть документ')
      );
    }
    if (previewRequest.current === requestId) {
      setPreviewLoading(false);
    }
  };

  const previewIndex = previewDoc
    ? previewFiles.findIndex((item) => item.id === previewDoc.id)
    : -1;

  const goPreview = (index: number) => {
    const doc = previewFiles[index];
    if (!doc) return;
    void handlePreview(doc, previewFiles);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleDelete = async (doc: UserDocument) => {
    if (!canEdit) return;
    if (!window.confirm(`Удалить «${doc.original_name}»?`)) return;
    try {
      await deleteUserDocument(userId, doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      if (previewDoc?.id === doc.id) {
        closePreview();
      }
      showSuccess('Документ удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить документ'));
    }
  };

  return (
    <div className="profile-docs-wrap">
      {feedbackNode}
      <div className="profile-docs-card">
        <div className="profile-docs-section-head">
          <div>
            <h3>Документы</h3>
            <p className="profile-docs-section-hint">
              PDF, фото или Word — до 10 МБ. Новый файл свидетельства, ВЛЭК
              или проверки навыков заменяет предыдущий. Иные документы
              копятся и открываются одним списком.
            </p>
          </div>
        </div>

        {canEdit ? (
          <div className="profile-docs-upload">
            <label className="profile-doc-type">
              Тип
              <select
                value={docType}
                onChange={(e) =>
                  setDocType(e.target.value as UserDocumentType)
                }
              >
                {DOC_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="btn-profile-docs-upload">
              {uploading ? 'Загрузка...' : 'Прикрепить'}
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx"
                multiple
                disabled={uploading}
                onChange={handleUpload}
                hidden
              />
            </label>
          </div>
        ) : null}

        {groups.length === 0 ? (
          <p className="profile-docs-empty">Документов пока нет</p>
        ) : (
          <ul className="profile-docs-list">
            {groups.map((group) => (
              <li key={group.key} className="profile-doc-group">
                <div className="profile-doc-meta">
                  <strong>{docTypeLabel(group.doc_type)}</strong>
                  {group.created_at ? (
                    <span>{DateFormat(group.created_at, dateFormat)}</span>
                  ) : null}
                </div>
                <ul className="profile-doc-files">
                  {group.files.map((doc) => (
                    <li key={doc.id} className="profile-doc-file">
                      <button
                        type="button"
                        className="profile-doc-file-open"
                        onClick={() => void handlePreview(doc, group.files)}
                      >
                        <span className="profile-doc-file-name">
                          {doc.original_name}
                        </span>
                      </button>
                      <div className="profile-doc-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="btn-profile-doc danger"
                            onClick={() => handleDelete(doc)}
                          >
                            Удалить
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
      {previewDoc ? (
        <DocumentPreviewModal
          title={previewDoc.original_name || previewDoc.filename}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewFiles.length}
          onPrev={() => goPreview(previewIndex - 1)}
          onNext={() => goPreview(previewIndex + 1)}
          onDownload={() => void handleDownload(previewDoc)}
          onClose={closePreview}
        />
      ) : null}
      {pendingFiles ? (
        <ConfirmModal
          title="Заменить документ?"
          message={`Новый файл «${docTypeLabel(docType)}» заменит предыдущий. Старые файлы этого типа будут удалены.`}
          confirmLabel="Заменить"
          cancelLabel="Отмена"
          danger
          submitting={uploading}
          onConfirm={() => {
            const files = pendingFiles;
            setPendingFiles(null);
            void uploadFiles(files, docType);
          }}
          onCancel={() => setPendingFiles(null)}
        />
      ) : null}
    </div>
  );
};

export default ProfileDocumentsFiles;
